import * as Sentry from '@sentry/react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AccountLayout } from '@/app/layouts/account-layout';
import { AppLayout } from '@/app/layouts/app-layout';
import { PlantElement } from '@/app/providers/router/ui/plant-element';
import { useRoutes } from '@/entities/navigation';
import { AgreementsSettingsPage } from '@/pages/agreements-settings';
import { AlarmManagementPage } from '@/pages/alarm-management';
import { AutomatedPlanAllTasksPage, AutomatedPlanPage } from '@/pages/automated-plan';
import { DashboardPage } from '@/pages/dashboard';
import { EditTaskPage } from '@/pages/edit-task';
import { EditYachtPage } from '@/pages/edit-yacht';
import { FaqPage } from '@/pages/faq';
import { GroupBrandDetailsPage, GroupManagementPage, GroupTeamDetailsPage } from '@/pages/group-management';
import { GroupManagementMembersPage } from '@/pages/group-management/ui/group-management-members-page';
import { HistoricalPage } from '@/pages/historical';
import { MultiYachtViewPage } from '@/pages/multi-yacht-view';
import { OilAnalysisPage } from '@/pages/oil-analysis';
import { PrivacyPolicyPage } from '@/pages/privacy-policy';
import { ProfilePage } from '@/pages/profile';
import { ProfileSettingsPage } from '@/pages/profile-settings';
import { TaskCreationPage } from '@/pages/task-creation';
import { TermsOfServicePage } from '@/pages/terms-of-service';
import { VirtualCockpitPage } from '@/pages/virtual-cockpit';
import { YachtTrackPage } from '@/pages/yacht-track';
import { YachtViewAreaPage } from '@/pages/yacht-view-area';
import { YachtsFleetPage } from '@/pages/yachts-fleet';
import { AppRoutes } from '@/shared/constants/app-routes.constant';
import { SUPPORT_LINK } from '@/shared/constants/support-link.constant';

import { AutomatedPlanElement } from '../automated-plan-element';
import { GroupManagementElement } from '../group-management-element';
import { HomeDefault } from '../home-default';
import { MobileElement } from '../mobile-element';
import { Redirect } from '../redirect';
import { RoleElement } from '../role-element';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(({ children }) => <Routes>{children}</Routes>);

const RouterComponent = () => {
	const { multiView, singleView, management, editManagement } = useRoutes();

	return (
		<SentryRoutes>
			<Route element={<HomeDefault />} path={AppRoutes.Home} />
			<Route element={<AppLayout />}>
				<Route element={<MultiYachtViewPage />} path={multiView} />
			</Route>
			<Route element={<AppLayout withSidebar />}>
				<Route element={<DashboardPage />} path={AppRoutes.Dashboard} />
				<Route element={<HistoricalPage />} path={AppRoutes.HistoricalData} />
				<Route element={<VirtualCockpitPage />} path={AppRoutes.VirtualCockpit} />
				<Route
					element={
						<AutomatedPlanElement>
							<AutomatedPlanPage />
						</AutomatedPlanElement>
					}
					path={AppRoutes.AutomatedPlan}
				/>
				<Route
					element={
						<AutomatedPlanElement>
							<AutomatedPlanAllTasksPage />
						</AutomatedPlanElement>
					}
					path={AppRoutes.AutomatedPlanAllTasks}
				/>
				<Route
					element={
						<AutomatedPlanElement>
							<TaskCreationPage />
						</AutomatedPlanElement>
					}
					path={AppRoutes.TaskCreation}
				/>
				<Route
					element={
						<AutomatedPlanElement>
							<EditTaskPage />
						</AutomatedPlanElement>
					}
					path={AppRoutes.EditTask}
				/>
				<Route element={<OilAnalysisPage />} path={AppRoutes.OilAnalysis} />
				<Route element={<YachtViewAreaPage />} path={singleView} />
				<Route
					element={
						<PlantElement>
							<YachtTrackPage />
						</PlantElement>
					}
					path={AppRoutes.YachtTrack}
				/>
				<Route element={<Redirect externalLink={SUPPORT_LINK} />} path={AppRoutes.Support} />
				<Route element={<Redirect externalLink={SUPPORT_LINK} />} path={AppRoutes.HelpCenter} />
				<Route element={<AlarmManagementPage />} path={AppRoutes.AlarmManagement} />
			</Route>
			<Route element={<AccountLayout />}>
				<Route element={<PrivacyPolicyPage />} path={AppRoutes.Privacy} />
				<Route element={<TermsOfServicePage />} path={AppRoutes.TermsOfService} />
				<Route element={<ProfilePage />} path={AppRoutes.Profile} />
				<Route
					element={
						<MobileElement>
							<ProfileSettingsPage />
						</MobileElement>
					}
					path={AppRoutes.Settings}
				/>
				<Route
					element={
						<RoleElement>
							<AgreementsSettingsPage />
						</RoleElement>
					}
					path={AppRoutes.AgreementsSettings}
				/>
				<Route
					element={
						<RoleElement>
							<YachtsFleetPage />
						</RoleElement>
					}
					path={management}
				/>
				<Route
					element={
						<RoleElement>
							<EditYachtPage />
						</RoleElement>
					}
					path={editManagement}
				/>
				<Route
					element={
						<GroupManagementElement>
							<GroupManagementPage />
						</GroupManagementElement>
					}
					path={AppRoutes.GroupManagement}
				/>
				<Route
					element={
						<GroupManagementElement>
							<GroupManagementMembersPage />
						</GroupManagementElement>
					}
					path={AppRoutes.GroupManagementMembers}
				/>
				<Route
					element={
						<GroupManagementElement>
							<GroupBrandDetailsPage />
						</GroupManagementElement>
					}
					path={AppRoutes.GroupManagementBrandDetails}
				/>
				<Route
					element={
						<GroupManagementElement>
							<GroupTeamDetailsPage />
						</GroupManagementElement>
					}
					path={AppRoutes.GroupManagementTeamDetails}
				/>
				<Route
					element={
						<MobileElement>
							<FaqPage />
						</MobileElement>
					}
					path={AppRoutes.Faq}
				/>
				<Route element={<TermsOfServicePage />} path={AppRoutes.FaqTermsOfService} />
				<Route element={<PrivacyPolicyPage />} path={AppRoutes.FaqPrivacy} />
			</Route>
			<Route element={<Navigate to={AppRoutes.Home} />} path="*" />
		</SentryRoutes>
	);
};

export default RouterComponent;
